<template>
  <div class="genre-nav-wrap" ref="rootRef" @mouseenter="open = true" @mouseleave="open = false">
    <NuxtLink
      :to="primaryGenre ? `/genre/${primaryGenre.slug}` : '/'"
      class="shop-nav-link"
      :class="{ active: isActive, 'is-open': open }"
      @click.prevent="toggle"
    >
      {{ $t('shop.nav_genres') }}
      <svg class="caret" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
    </NuxtLink>

    <Transition name="genre-mega">
      <div v-if="open && mainGenres.length" class="genre-mega" @click="open = false">
        <div class="genre-mega-head">
          <span class="genre-mega-eyebrow">{{ $t('shop.browse_by_genre', 'Browse by Genre') }}</span>
          <span class="genre-mega-counter">{{ genres.length }}</span>
        </div>

        <div class="genre-mega-scroll">
          <div class="genre-mega-grid">
            <NuxtLink
              v-for="g in mainGenres"
              :key="g.slug"
              :to="`/genre/${g.slug}`"
              class="genre-tile"
              :style="{ '--tile-hue': hueFor(g.name) }"
            >
              <img
                v-if="g.artwork"
                :src="proxyArt(g.artwork)"
                alt=""
                class="genre-tile-art"
                loading="lazy"
              />
              <span class="genre-tile-overlay"></span>
              <span class="genre-tile-text">
                <span class="genre-tile-name">{{ g.name }}</span>
                <span v-if="g.total" class="genre-tile-count">
                  {{ g.total }} {{ g.total === 1 ? $t('shop.release', 'release') : $t('shop.releases', 'releases') }}
                </span>
              </span>
              <span class="genre-tile-arrow">→</span>
            </NuxtLink>
          </div>

          <!-- Sub-genres section. Hidden when no main genre has any subs
               (most tenants in early days). Rendered as compact chips
               grouped by parent so users see "Trance: Psy-Trance · Goa
               Trance" rather than flat-listing them next to main genres. -->
          <div v-if="subGenreGroups.length" class="genre-mega-subs">
            <span class="genre-mega-eyebrow">{{ $t('shop.sub_genres', 'Sub-Genres') }}</span>
            <div
              v-for="group in subGenreGroups"
              :key="group.parent.slug"
              class="genre-sub-group"
            >
              <span class="genre-sub-parent">{{ group.parent.name }}</span>
              <div class="genre-sub-chips">
                <NuxtLink
                  v-for="s in group.subs"
                  :key="s.slug"
                  :to="`/genre/${s.slug}`"
                  class="genre-sub-chip"
                >
                  {{ s.name }}
                  <span v-if="s.total" class="genre-sub-chip-count">{{ s.total }}</span>
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const route = useRoute()
const rootRef = ref(null)
const open = ref(false)

// Public, anonymous-safe endpoint. The earlier version used `/api/genres`
// which requires a user session and 401-ed during SSR for shop visitors —
// breaking the entire header for anonymous traffic. `/api/shop/genres`
// returns deduplicated, cross-tenant rows already shaped for the menu.
const { data: genres } = await useFetch('/api/shop/genres', {
  key: 'shop-genres',
  default: () => []
})

const primaryGenre = computed(() => genres.value?.[0] || null)

// Only top-level genres render as tiles. Sub-genres show below as chips
// grouped by their parent. The API tags each entry with `parentKey` —
// null/undefined → main genre, set → sub-genre belonging to that key.
const mainGenres = computed(() => (genres.value || []).filter(g => !g.parentKey))

// Build [{ parent, subs }] only for parents that actually have subs.
// Saves the template a v-if and keeps the section hidden entirely when
// the catalog has no nested genres yet.
const subGenreGroups = computed(() => {
  return mainGenres.value
    .filter(g => Array.isArray(g.subGenres) && g.subGenres.length)
    .map(g => ({ parent: g, subs: g.subGenres }))
})

const isActive = computed(() => route.path.startsWith('/genre'))

const toggle = () => { open.value = !open.value }

// Per-name colour hash so each tile picks a stable hue. Same djb2-ish
// recipe the shop landing's genre tiles use, so a genre keeps the same
// accent colour whether the user sees it in the mega menu or on /.
const hueFor = (name) => {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return String(h % 360)
}

// Server-side artwork URLs are remote (S3/Wasabi). Route them through the
// asset proxy so private buckets work and CORS doesn't 403 the image in
// the browser. Identical helper to the landing's `proxyArt`.
const proxyArt = (url) => {
  if (!url) return ''
  if (url.startsWith('http')) return `/api/assets/serve?url=${encodeURIComponent(url)}`
  return url
}

const onClickOutside = (e) => {
  if (rootRef.value && !rootRef.value.contains(e.target)) open.value = false
}

onMounted(() => document.addEventListener('click', onClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside))
</script>

<style scoped>
.genre-nav-wrap {
  position: relative;
}

/* Typography/colour grammar for .shop-nav-link lives in shop-cyber.css
   (`.shop-layout .shop-nav .shop-nav-link`) — the global source wins on
   specificity and keeps this duplicate in sync with ShopHeader's links.
   Only the structural props this trigger needs on top stay here. */
.shop-nav-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s ease;
}
.shop-nav-link:hover, .shop-nav-link.is-open {
  color: var(--text-main);
}

.caret {
  width: 10px;
  height: 10px;
  opacity: 0.6;
  transition: transform 0.15s;
}
.shop-nav-link.is-open .caret {
  transform: rotate(180deg);
}

/* ─── Mega-menu container ──────────────────────────────────────────
   A HUD module hanging off the nav: near-black module ground, hairline
   rim, corner brackets on the two free corners (drawn as pseudo
   elements — TR + BL, matching CyberPanel's bracket convention).
   Chrome may blur (refined-look exemption 3); lists inside it are
   solid. Width fits 2 cover tiles at desktop, 1 at narrow. */
.genre-mega {
  position: absolute;
  top: calc(100% + 8px);
  left: -12px;
  width: 540px;
  max-width: 92vw;
  background: color-mix(in srgb, var(--cy-module) 92%, transparent);
  border: 1px solid var(--cy-rim);
  border-radius: 3px;
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.7),
    var(--cy-glow);
  z-index: 200;
  max-height: 78vh;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}
/* Corner brackets — 2px × 12px, top-right + bottom-left. */
.genre-mega::before,
.genre-mega::after {
  content: '';
  position: absolute;
  width: 12px;
  height: 12px;
  pointer-events: none;
  border: 0 solid var(--cy-rim-hot);
}
.genre-mega::before { top: -1px; right: -1px; border-top-width: 2px; border-right-width: 2px; }
.genre-mega::after  { bottom: -1px; left: -1px; border-bottom-width: 2px; border-left-width: 2px; }

.genre-mega-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 9px;
  border-bottom: 1px solid var(--cy-line);
}

.genre-mega-eyebrow {
  font-family: var(--cy-mono);
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--primary);
}

.genre-mega-counter {
  font-family: var(--cy-mono);
  font-size: 0.62rem;
  font-weight: 600;
  color: var(--text-muted);
  background: rgb(var(--primary-channels) / 0.08);
  border: 1px solid var(--cy-rim);
  border-radius: 2px;
  padding: 1px 7px;
  font-variant-numeric: tabular-nums;
}

.genre-mega-scroll {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(78vh - 60px);  /* leave room for the header */
  scrollbar-width: thin;
  scrollbar-color: rgb(var(--primary-channels) / 0.35) transparent;
}
.genre-mega-scroll::-webkit-scrollbar { width: 4px; }
.genre-mega-scroll::-webkit-scrollbar-thumb {
  background: rgb(var(--primary-channels) / 0.35);
}

.genre-mega-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 12px;
}

/* ─── Sub-genre section ─────────────────────────────────────────── */
.genre-mega-subs {
  padding: 8px 14px 16px;
  border-top: 1px solid var(--cy-line);
  background: color-mix(in srgb, var(--cy-ground) 45%, transparent);
}

.genre-mega-subs .genre-mega-eyebrow {
  display: block;
  margin-bottom: 10px;
}

.genre-sub-group {
  display: grid;
  grid-template-columns: 110px 1fr;
  align-items: start;
  gap: 10px;
  padding: 8px 0;
  border-top: 1px dashed var(--cy-line);
}
.genre-sub-group:first-of-type { border-top: none; }

.genre-sub-parent {
  font-family: var(--cy-display);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);
  padding-top: 4px;
}

.genre-sub-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* Notched mono chips — the shop's replacement for rounded pills
   (same cut CyberChip uses; clip-path keeps the corner honest). */
.genre-sub-chip {
  --cy-n: 6px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  clip-path: polygon(
    var(--cy-n) 0, 100% 0,
    100% calc(100% - var(--cy-n)), calc(100% - var(--cy-n)) 100%,
    0 100%, 0 var(--cy-n)
  );
  font-family: var(--cy-mono);
  font-size: 0.64rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  text-decoration: none;
  background: rgb(var(--primary-channels) / 0.06);
  transition: color 0.12s ease, background 0.12s ease;
}
.genre-sub-chip:hover {
  color: var(--primary);
  background: rgb(var(--primary-channels) / 0.16);
}

.genre-sub-chip-count {
  font-size: 0.58rem;
  font-weight: 700;
  opacity: 0.7;
  font-variant-numeric: tabular-nums;
}

@media (max-width: 600px) {
  .genre-mega { left: 0; width: calc(100vw - 24px); }
  .genre-mega-grid { grid-template-columns: 1fr; }
}

/* ─── Per-genre tile ─────────────────────────────────────────────
   Keeps the per-name hue hash + artwork recipe (data-driven colour,
   matches the landing's genre tiles) — the frame sharpens onto the
   module grammar: 3px radius, hairline that ignites in the tile hue. */
.genre-tile {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  height: 64px;
  padding: 10px 12px;
  border-radius: 3px;
  text-decoration: none;
  overflow: hidden;
  border: 1px solid var(--cy-line);
  background: color-mix(in srgb, var(--cy-ground) 55%, transparent);
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease;
}

.genre-tile-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.35;
  filter: saturate(1.1);
  transition: opacity 0.25s ease, transform 0.45s ease;
  z-index: 0;
}

.genre-tile-overlay {
  position: absolute;
  inset: 0;
  background:
    /* Hue tint pulled left so the text column reads cleanly. */
    linear-gradient(90deg,
      hsl(var(--tile-hue) 75% 22% / 0.85) 0%,
      hsl(var(--tile-hue) 70% 20% / 0.55) 45%,
      transparent 100%
    ),
    /* Bottom darkening so the count line always sits on a dark stripe. */
    linear-gradient(180deg, transparent 40%, rgba(0, 0, 0, 0.55) 100%);
  z-index: 1;
  transition: opacity 0.2s ease;
}

.genre-tile-text {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.genre-tile-name {
  font-family: var(--cy-display);
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--text-main);
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.65);
}

.genre-tile-count {
  font-family: var(--cy-mono);
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--text-main) 75%, transparent);
  font-variant-numeric: tabular-nums;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

.genre-tile-arrow {
  position: relative;
  z-index: 2;
  font-size: 1rem;
  color: color-mix(in srgb, var(--text-main) 60%, transparent);
  transform: translateX(-4px);
  opacity: 0;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.genre-tile:hover {
  transform: translateY(-1px);
  border-color: hsl(var(--tile-hue) 80% 55% / 0.45);
  background: color-mix(in srgb, var(--cy-ground) 40%, transparent);
}
.genre-tile:hover .genre-tile-art {
  opacity: 0.55;
  transform: scale(1.06);
}
.genre-tile:hover .genre-tile-arrow {
  opacity: 1;
  transform: translateX(0);
  color: var(--text-main);
}

.genre-tile:focus-visible {
  outline: 2px solid hsl(var(--tile-hue) 80% 55%);
  outline-offset: 2px;
}

/* ─── Transition ──────────────────────────────────────────────── */
/* Enter snaps in (180 ms) so the menu feels responsive on hover, but
   the leave is stretched (550 ms) with an ease-out curve so the
   dropdown drifts away gracefully instead of disappearing the moment
   the cursor leaves. */
.genre-mega-enter-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.genre-mega-leave-active {
  transition: opacity 0.55s ease-out, transform 0.55s ease-out;
}
.genre-mega-enter-from, .genre-mega-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (prefers-reduced-motion: reduce) {
  .genre-mega-enter-active,
  .genre-mega-leave-active { transition: opacity 0.12s ease; }
  .genre-tile:hover { transform: none; }
}
</style>
